'use client'

import { PictureTshirt24, Plus24 } from '@vinted/monochrome-icons'
import { Button, Cell, Icon, Label, Loader, Spacer, Text, Validation } from '@vinted/web-ui'
import { isEmpty, noop, take } from 'lodash'
import { ChangeEvent, Fragment, ReactNode, RefObject, useEffect, useRef, useState } from 'react'

import {
  ItemUploadFieldName,
  useFieldVisibility,
} from '@marketplace-web/item-upload/item-upload-data'
import { PhotoTips } from '@marketplace-web/item-upload/photo-tips-feature'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useAbTest, useTrackAbTest } from '@marketplace-web/feature-flags/ab-tests-data'
import { Dropzone } from '@marketplace-web/common-components/dropzone-ui'
import { useLatestCallback, UiState } from '@marketplace-web/shared/ui-state-util'
import { ItemAlertStatus } from '@marketplace-web/item-page/item-status-data'
import {
  ImageOrientation,
  MediaSelectPhotoOrientation,
  MediaUploadPhotoModel,
} from '@marketplace-web/item-upload/media-upload-data'

import { MAXIMUM_FILE_SIZE_BYTES, ONE_MEGABYTE_IN_BYTES } from '../../constants'
import { resizePhoto } from '../../utils'
import useMediaSelectContext from '../../hooks/useMediaSelectContext'
import useMediaSelectSort from '../../hooks/useMediaSelectSort'
import useMediaSelectUtils from '../../hooks/useMediaSelectUtils'
import SortableList from '../SortableList'
import { MediaSelectType } from './constants'
import PhotoBox from './PhotoBox'

type Props = {
  tempUuid?: string
  numberOfImagesPerItem: number
  enableRotation?: boolean
  enablePhotoTips?: boolean
  enableDescription?: boolean
  type: MediaSelectType
  hideWhenEmpty?: boolean
  photoEntryId: number | string | null
  inputRef?: RefObject<HTMLInputElement | null>
  customValidationMessage?: string | null
  submitPhotoTips?: string | null
  title?: ReactNode
  uploadButtonText?: string
  uploadButtonSize?: React.ComponentProps<typeof Button>['size']
  onBlur?: () => void
  onFocus?: () => void
  onDelete?: (id: number) => void
  renderBanner?: () => ReactNode
  alertStatus?: ItemAlertStatus | null
  photoTipBanner?: {
    message: string
    actionTitle: string
  } | null
  onUploadFilesStart?: () => void
  onUploadFilesEnd?: (uiState: UiState, photosCount: number, userItemCount: number) => void
  onPhotoSortEnd?: (oldIndex: number, newIndex: number) => void
  onRotate?: (index: number) => void
  enableImageResizing?: boolean
  onPhotoFetchError?: () => void
}

const MediaSelect = ({
  tempUuid,
  numberOfImagesPerItem,
  enableRotation = false,
  enablePhotoTips = false,
  enableDescription = false,
  type,
  hideWhenEmpty = false,
  photoEntryId,
  inputRef,
  customValidationMessage,
  submitPhotoTips,
  title,
  onBlur = noop,
  onFocus = noop,
  onDelete = noop,
  onPhotoFetchError = noop,
  uploadButtonText,
  uploadButtonSize,
  renderBanner,
  alertStatus,
  photoTipBanner,
  onUploadFilesStart = noop,
  onUploadFilesEnd = noop,
  onPhotoSortEnd = noop,
  onRotate = noop,
  enableImageResizing = false,
}: Props) => {
  const translate = useTranslate('item_upload')
  const translateDropzone = useTranslate()

  const photoUploadMainPhotoChipAbTest = useAbTest('photo_upload_main_photo_chip')
  useTrackAbTest(photoUploadMainPhotoChipAbTest)
  const isMainPhotoBadgeEnabled = photoUploadMainPhotoChipAbTest?.variant === 'on'

  const {
    photos,
    photoOrientations,
    photoTips,
    uiStateWithSetter: [uiState, setUiState],
    photosCount,
    removePhoto,
    setPhotoOrientation,
    initialPhotosPresent,
  } = useMediaSelectContext()

  const [isControlsHidden, setIsControlsHidden] = useState(false)
  const [isFileSizeExceeded, setIsFileSizeExceeded] = useState(false)
  const [isPhotoTipsOpen, setIsPhotoTipsOpen] = useState(false)
  const [isNativeUploadOpen, setIsNativeUploadOpen] = useState(false)

  const removePhotoTipsAbTest = useAbTest('photo_upload_remove_photo_tips')
  useTrackAbTest(removePhotoTipsAbTest)
  const isRemovePhotoTipsAbTestEnabled = removePhotoTipsAbTest?.variant === 'on'

  const containerRef = useRef<HTMLDivElement>(null)
  const localInputRef = useRef<HTMLInputElement>(null)
  const fileInputRef = inputRef || localInputRef
  const isUploading = uiState === UiState.Pending
  const isHidden = hideWhenEmpty && isEmpty(photos)

  const { fetchPhotos, uploadPhotoFiles } = useMediaSelectUtils({
    photoEntryId,
    tempUuid,
    type,
    setUiState,
    photosCount,
    onUploadFilesStart,
    onUploadFilesEnd,
    onPhotoFetchError,
  })
  const { ariaText, handleSortStart, handleSortEnd, handleSortOver } = useMediaSelectSort({
    setIsControlsHidden,
    containerRef,
    photosCount,
    onSortEnd: onPhotoSortEnd,
  })

  const latestOnBlur = useLatestCallback(onBlur)
  useFieldVisibility({ fieldName: ItemUploadFieldName.Photos, isVisible: !isHidden })

  useEffect(() => {
    const handleWindowFocus = () => {
      if (!isNativeUploadOpen) return

      setIsNativeUploadOpen(false)
      latestOnBlur()
    }

    window.addEventListener('focus', handleWindowFocus)

    return () => window.removeEventListener('focus', handleWindowFocus)
  }, [isNativeUploadOpen, latestOnBlur])

  useEffect(() => {
    if (initialPhotosPresent) return

    fetchPhotos()
  }, [fetchPhotos, initialPhotosPresent])

  const handleTipsOpen = () => setIsPhotoTipsOpen(true)

  const handleTipsClose = () => setIsPhotoTipsOpen(false)

  const showFileSelectionPrompt = () => {
    if (!fileInputRef.current) return

    setIsNativeUploadOpen(true)
    onFocus()
    fileInputRef.current.click()
  }

  const uploadFiles = async (files: Array<File>) => {
    if (isUploading || files.length === 0) return

    if (numberOfImagesPerItem <= photosCount) return

    if (files.some(file => file.size > MAXIMUM_FILE_SIZE_BYTES)) {
      setIsFileSizeExceeded(true)

      return
    }

    setIsFileSizeExceeded(false)

    let filesToUpload = take(files, numberOfImagesPerItem - photosCount)

    if (enableImageResizing) {
      filesToUpload = await Promise.all(
        filesToUpload.map(file => (file.size >= ONE_MEGABYTE_IN_BYTES ? resizePhoto(file) : file)),
      )
    }

    uploadPhotoFiles({ files: filesToUpload, uploadType: type })

    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handlePhotoDelete = (id: number) => {
    if (!fileInputRef.current) return

    removePhoto(id)
    onDelete(id)
    fileInputRef.current.value = ''
  }

  const handleFileInputChange = async (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return

    const files = Array.from(event.target.files)

    await uploadFiles(files)
  }

  const handleSetPhotoOrientation =
    (index: number) => (photoOrientation: MediaSelectPhotoOrientation) => {
      setPhotoOrientation(photoOrientation)
      onRotate(index)
    }

  const renderFileSizeError = () => {
    if (!isFileSizeExceeded) return null

    return <Validation text={translate('media_select.size_limit')} theme="warning" />
  }

  const renderCustomError = () => {
    if (!customValidationMessage) return null

    return <Validation text={customValidationMessage} theme="warning" />
  }

  const renderWithSpacer = (child: ReactNode) => {
    if (!child) return null

    return (
      <>
        <Spacer size="medium" />
        {child}
      </>
    )
  }

  const renderNote = () => {
    if (alertStatus === ItemAlertStatus.ReplicaProof) {
      return <Text as="h4" type="caption" text={translate('media_select.replica_proof_label')} />
    }

    if (photosCount < 2) return null

    return <Text as="h4" type="caption" text={translate('media_select.reordering_label')} />
  }

  const renderPhotoTips = () => {
    if (!submitPhotoTips) return null

    return <Text as="h4" type="caption" text={submitPhotoTips} />
  }

  const renderUploadButtonBox = () => {
    if (photosCount === numberOfImagesPerItem) return null

    if (isUploading) {
      return (
        <div data-testid="upload-button-loader" className="media-select__input-box">
          <div className="media-select__input-box-content">
            <Loader size="x-large" />
          </div>
        </div>
      )
    }

    if (photosCount < 1) return null

    return (
      <div data-testid="add-photos-icon-button" className="media-select__input-box">
        <div className="media-select__input-box-content">
          <Button
            onClick={showFileSelectionPrompt}
            icon={<Icon name={Plus24} testId="plus" color="primary-default" />}
            inline
            aria={{
              'aria-label': translate('media_select.a11y.add_photo'),
            }}
          />
        </div>
      </div>
    )
  }

  const renderUploadButtonArea = () => {
    if (photosCount > 0 || isUploading) return null

    return (
      <div className="media-select__input">
        <div className="media-select__input-content">
          <Button
            text={uploadButtonText || translate('media_select.upload_button')}
            onClick={showFileSelectionPrompt}
            icon={<Icon name={Plus24} testId="plus" color="primary-default" />}
            inline
            size={uploadButtonSize || 'default'}
          />
        </div>
      </div>
    )
  }

  const renderPhotoBox = (photo: MediaUploadPhotoModel, index: number) => (
    <Fragment key={photo.id}>
      <PhotoBox
        orientation={
          photoOrientations.find(photoOrientation => photoOrientation.id === photo.id)
            ?.orientation || ImageOrientation.Degree0
        }
        setPhotoOrientation={handleSetPhotoOrientation(index)}
        index={index}
        photo={photo}
        onDelete={handlePhotoDelete}
        isControlsHidden={isControlsHidden || isUploading}
        enableRotation={enableRotation}
        showMainBadge={isMainPhotoBadgeEnabled && index === 0}
        imageAlt={`${translate('media_select.a11y.uploaded_image', {
          count: index + 1,
          total: photosCount,
        })} ${translate('media_select.a11y.inactive_reorder')}`}
      />
      <span aria-live="assertive" className="u-visually-hidden">
        {ariaText}
      </span>
    </Fragment>
  )

  const renderImageGrid = () => (
    <Dropzone onDrop={uploadFiles} overlayDescription={translateDropzone('dropzone.description')}>
      <SortableList
        testId="media-select-grid"
        className="media-select__grid"
        onDragStart={handleSortStart}
        onDragOver={handleSortOver}
        onSortUpdate={handleSortEnd}
      >
        {photos.map(renderPhotoBox)}
        {renderUploadButtonBox()}
      </SortableList>

      {renderUploadButtonArea()}
    </Dropzone>
  )

  const renderImageInput = () => (
    <input
      data-testid="add-photos-input"
      name={ItemUploadFieldName.Photos}
      ref={fileInputRef}
      onChange={handleFileInputChange}
      className="u-hidden"
      type="file"
      accept="image/jpeg,image/gif,image/png,image/webp"
      multiple
    />
  )

  if (isHidden) {
    // we cannot render null, because <input /> could be triggered from forwarded ref
    return renderImageInput()
  }

  const renderPhotoTipsBanner = () => {
    if (!photoTipBanner || !enablePhotoTips || isRemovePhotoTipsAbTestEnabled) return null

    return (
      <div className="photo-tips__banner">
        <Cell
          styling="narrow"
          prefix={
            <>
              <Spacer orientation="horizontal" size="small" />
              <Icon name={PictureTshirt24} color="primary-default" />
            </>
          }
          body={
            <>
              <Text text={photoTipBanner.message} as="span" type="subtitle" />
              &nbsp;
              <button type="button" onClick={handleTipsOpen}>
                <Text
                  theme="primary"
                  as="span"
                  type="subtitle"
                  text={photoTipBanner.actionTitle}
                  clickable
                />
              </button>
            </>
          }
          theme="calypsoLightExperimental"
        />
      </div>
    )
  }

  return (
    <div data-testid="media-select" id={ItemUploadFieldName.Photos} ref={containerRef}>
      {renderImageInput()}
      {enableDescription && (
        <Label
          type="leading"
          styling="wide"
          text={
            <Text
              type="subtitle"
              as="span"
              text={translate('media_select.description', {
                max_number_of_files: numberOfImagesPerItem,
              })}
            />
          }
        />
      )}
      <Cell styling="wide">
        {title || null}
        {renderImageGrid()}

        {renderWithSpacer(
          renderFileSizeError() || renderCustomError() || renderNote() || renderPhotoTips() || null,
        )}

        {renderWithSpacer(renderPhotoTipsBanner())}

        <PhotoTips
          isOpen={isPhotoTipsOpen}
          photoTips={photoTips}
          handleClose={handleTipsClose}
          uploadSessionId={tempUuid}
        />
        {renderBanner?.()}
      </Cell>
    </div>
  )
}

export default MediaSelect
